import { getExperimentOverride } from "@/experimentManager/utils";
import useCanViewExperimentManager from "@/experimentManager/utils/useCanSeeExperimentManager";
import useUserExperimentSegments from "@/users/useUserExperimentSegments";

/**
 * @param {string} experimentName - The key of the experiment.
 * @returns {[string|object|undefined, {loading: Boolean, error: Error}]} An
 *  array containing first, whether or not the given feature is enabled or an object
 *  containing all enabled features, which is `undefined` if the decision is loading.
 *  Then next, an object with `loading`, and `error`. `loading` indicates whether
 *  the feature decision is potentially still loading. `error` includes any errors
 *  that may be related to properly determining feature access.
 *
 *  For users that have access to the experiment manager tool, this function
 *  would prioritize overwritten values returned by `getExperimentOverride`
 *  over the results of `useUserExperimentSegments` API call.
 */
const useGetUserExperimentSegment = (experimentName) => {
  const { data, loading, error } = useUserExperimentSegments();
  const canViewExperimentManager = useCanViewExperimentManager();

  if (!data || loading || error) {
    return [undefined, { loading, error }];
  }
  if (experimentName) {
    const experimentOverride = getExperimentOverride(experimentName);
    if (canViewExperimentManager && experimentOverride) {
      return [experimentOverride, { loading: false, error: null }];
    }
    return [data[experimentName], { loading, error }];
  } else {
    return [data, { loading, error }];
  }
};

export default useGetUserExperimentSegment;
